import { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, X, Save } from "lucide-react";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

export default function AdminAvailability() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newSlot, setNewSlot] = useState("");
  const [newClosed, setNewClosed] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/availability").then((r) => setCfg(r.data)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/admin/availability", cfg);
      toast.success("Configuration enregistrée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (i) =>
    setCfg((c) => ({
      ...c,
      open_days: c.open_days.includes(i) ? c.open_days.filter((x) => x !== i) : [...c.open_days, i].sort(),
    }));

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-coral" /></div>;

  return (
    <div data-testid="admin-availability-page">
      <h1 className="font-display text-4xl">Créneaux Click &amp; Collect</h1>
      <p className="text-amyu-muted mt-2">Configure les jours ouverts, créneaux et fermetures exceptionnelles.</p>

      <div className="mt-6 grid gap-4">
        <div className="bg-white rounded-[24px] p-5">
          <h2 className="font-display text-xl mb-3">Jours ouverts</h2>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d, i) => (
              <button
                key={d}
                onClick={() => toggleDay(i)}
                data-testid={`day-toggle-${i}`}
                className={`px-4 py-2 rounded-full text-sm ${
                  cfg.open_days.includes(i) ? "bg-coral text-white" : "bg-amyu-cream text-amyu-muted"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-[24px] p-5">
          <h2 className="font-display text-xl mb-3">Créneaux (HH:MM)</h2>
          <div className="flex flex-wrap gap-2 mb-3">
            {cfg.slots.map((s) => (
              <span key={s} className="inline-flex items-center gap-1 bg-amyu-pink pill px-3 py-1.5 text-sm">
                {s}
                <button
                  onClick={() => setCfg((c) => ({ ...c, slots: c.slots.filter((x) => x !== s) }))}
                  data-testid={`slot-remove-${s}`}
                  className="text-coral hover:text-coral/70"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="time"
              value={newSlot}
              onChange={(e) => setNewSlot(e.target.value)}
              data-testid="new-slot-input"
              className="bg-amyu-cream rounded-full px-4 py-2 outline-none"
            />
            <button
              onClick={() => {
                if (newSlot && !cfg.slots.includes(newSlot)) {
                  setCfg((c) => ({ ...c, slots: [...c.slots, newSlot].sort() }));
                  setNewSlot("");
                }
              }}
              data-testid="new-slot-add"
              className="btn-coral inline-flex items-center gap-1"
            >
              <Plus size={14} /> Ajouter
            </button>
          </div>
        </div>

        <div className="bg-white rounded-[24px] p-5">
          <h2 className="font-display text-xl mb-3">Fermetures exceptionnelles</h2>
          <div className="flex flex-wrap gap-2 mb-3">
            {cfg.closed_dates.map((d) => (
              <span key={d} className="inline-flex items-center gap-1 bg-amyu-cream pill px-3 py-1.5 text-sm">
                {d}
                <button
                  onClick={() => setCfg((c) => ({ ...c, closed_dates: c.closed_dates.filter((x) => x !== d) }))}
                  className="text-coral hover:text-coral/70"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            {cfg.closed_dates.length === 0 && <span className="text-sm text-amyu-muted">Aucune fermeture</span>}
          </div>
          <div className="flex gap-2">
            <input
              type="date"
              value={newClosed}
              onChange={(e) => setNewClosed(e.target.value)}
              className="bg-amyu-cream rounded-full px-4 py-2 outline-none"
            />
            <button
              onClick={() => {
                if (newClosed && !cfg.closed_dates.includes(newClosed)) {
                  setCfg((c) => ({ ...c, closed_dates: [...c.closed_dates, newClosed].sort() }));
                  setNewClosed("");
                }
              }}
              className="btn-coral inline-flex items-center gap-1"
            >
              <Plus size={14} /> Fermer
            </button>
          </div>
        </div>

        <div className="bg-white rounded-[24px] p-5 grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-amyu-muted">Délai min. (jours)</label>
            <input
              type="number"
              min="0"
              value={cfg.lead_time_days}
              onChange={(e) => setCfg((c) => ({ ...c, lead_time_days: parseInt(e.target.value || "0", 10) }))}
              data-testid="lead-time-input"
              className="mt-1.5 w-full bg-amyu-cream rounded-2xl px-4 py-2 outline-none"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-amyu-muted">Adresse point de retrait</label>
            <input
              value={cfg.pickup_location}
              onChange={(e) => setCfg((c) => ({ ...c, pickup_location: e.target.value }))}
              data-testid="pickup-location-input"
              className="mt-1.5 w-full bg-amyu-cream rounded-2xl px-4 py-2 outline-none"
            />
          </div>
        </div>

        <button
          onClick={save}
          disabled={saving}
          data-testid="availability-save"
          className="btn-coral inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
        </button>
      </div>
    </div>
  );
}
